import React, { useRef, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { selectCurrentYear, setCurrentYear } from "@store/mainReducer";
import styled from "styled-components";

const YearSwitcherContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  @media (max-width: 768px) and (min-width: 501px) {
    gap: 0.25rem;
  }
`;

const YearLabel = styled.span<{ active: boolean }>`
  font-size: 0.8rem;
  font-weight: ${(props) => (props.active ? "bold" : "normal")};
  color: ${(props) => (props.active ? "#42BCDF" : "white")};
  opacity: ${(props) => (props.active ? "1" : "0.5")};
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
  transition: all 0.3s ease;

  &:hover {
    color: #42bcdf;
    opacity: 1;
  }

  @media (max-width: 768px) and (min-width: 501px) {
    font-size: 0.65rem;
  }
  @media (max-width: 500px) {
    font-size: 0.7rem;
  }
`;

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 8px;
  background: white;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  touch-action: none;

  @media (max-width: 768px) and (min-width: 501px) {
    height: 6px;
  }
`;

const YearsContainer = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  justify-items: center;

  & > :first-child {
    justify-self: start;
  }

  & > :last-child {
    justify-self: end;
  }
`;

const SliderThumb = styled.div<{ currentYear: string }>`
  position: absolute;
  width: 30px;
  height: 16px;
  background: #42bcdf;
  border-radius: 15px;
  transition: left 0.3s ease;
  left: ${(props) => {
    if (props.currentYear === "12/2024") return "0";
    if (props.currentYear === "06/2025") return "calc(30% - 15px)";
    if (props.currentYear === "11/2025") return "calc(50% - 15px)";
    if (props.currentYear === "01/2026") return "calc(70% - 15px)";
    return "calc(100% - 30px)";
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) and (min-width: 501px) {
    width: 20px;
    height: 12px;
    left: ${(props) => {
      if (props.currentYear === "12/2024") return "0";
      if (props.currentYear === "06/2025") return "calc(30% - 10px)";
      if (props.currentYear === "11/2025") return "calc(50% - 10px)";
      if (props.currentYear === "01/2026") return "calc(70% - 10px)";
      return "calc(100% - 20px)";
    }};
  }
`;

const YearSwitcher = () => {
  const dispatch = useAppDispatch();
  const currentYear = useAppSelector(selectCurrentYear);
  const sliderRef = useRef<HTMLDivElement>(null);

  const handleYearChange = (year: string) => {
    dispatch(setCurrentYear(year));
  };

  const getYearFromPosition = useCallback((clientX: number) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const posX = clientX - rect.left;
    const percentage = (posX / rect.width) * 100;

    if (percentage < 20) {
      handleYearChange("12/2024");
    } else if (percentage < 40) {
      handleYearChange("06/2025");
    } else if (percentage < 60) {
      handleYearChange("11/2025");
    } else if (percentage < 80) {
      handleYearChange("01/2026");
    } else {
      handleYearChange("06/2026");
    }
  }, []);

  const handleSliderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    getYearFromPosition(e.clientX);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    getYearFromPosition(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    getYearFromPosition(e.touches[0].clientX);
  };

  return (
    <YearSwitcherContainer>
      <SliderContainer
        ref={sliderRef}
        onClick={handleSliderClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      >
        <SliderThumb currentYear={currentYear}></SliderThumb>
      </SliderContainer>
      <YearsContainer>
        <YearLabel
          active={currentYear === "12/2024"}
          onClick={() => handleYearChange("12/2024")}
        >
          12/2024
        </YearLabel>

        <YearLabel
          active={currentYear === "06/2025"}
          onClick={() => handleYearChange("06/2025")}
        >
          06/2025
        </YearLabel>

        <YearLabel
          active={currentYear === "11/2025"}
          onClick={() => handleYearChange("11/2025")}
        >
          11/2025
        </YearLabel>

        <YearLabel
          active={currentYear === "01/2026"}
          onClick={() => handleYearChange("01/2026")}
        >
          01/2026
        </YearLabel>

        <YearLabel
          active={currentYear === "06/2026"}
          onClick={() => handleYearChange("06/2026")}
        >
          06/2026
        </YearLabel>
      </YearsContainer>
    </YearSwitcherContainer>
  );
};

export default YearSwitcher;
